// Inspector.jsx
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './style.css';
import visibleIcon from '../menuBar/assets/ico-visible.svg';
import hiddenIcon from '../menuBar/assets/ico-hidden.svg';
import DefaultInspector from './default/SwitchInspector';
import ConnectingCableInspector from './connectingCable/ConnectingCableInspector';
import DistributionBoardInspector from './distributionBoard/DistributionBoardInspector';
import BoxInspector from './box/BoxInspector';
import { useNodeOperations } from '../../../../hooks/useNodeOperations';
import { getInspectorDefinition, resolveInspectorForNode, INSPECTOR_IDS, getNodeLabelOverrideKey } from '../../../../utilities/inspectors/registry';
import Symbols from '../../../../utilities/mappings/symbols/Symbols';
import { clearAttributeFocus, setScene } from '../../../../redux/reducers/global/inspector/inspectorSlice';
import { setNodeHideAttributeLabels, setNodeAttributes } from '../../../../redux/reducers/global/node/nodeSlice';
import { setTabPanel } from '../../../../redux/reducers/global/editor/editorSlice';
import { addAlert } from '../../../../redux/reducers/global/alerts/alertSlice';
import { setSymbolLabelOverride } from '../../../../redux/reducers/global/view/viewSlice';
import EditableInspectorLabel from './EditableInspectorLabel';
import { EDITOR_TAB_PANEL_INSPECTOR } from '../../../../utilities/constants/editorConstants';

const TWO_WINDING_VARIANT_LABELS = {
  'transfomador-de-2-devanados-Base': 'Base',
  'transfomador-de-2-devanados-DeltaDelta': 'Delta-Delta',
  'transfomador-de-2-devanados-DeltaEstrella': 'Delta-Estrella',
  'transfomador-de-2-devanados-DeltaEstrella-Aterrizado': 'Delta-Estrella Aterrizada',
  'transfomador-de-2-devanados-EstrellaEstrella': 'Estrella-Estrella',
  'transfomador-de-2-devanados-Impedancia': 'Delta-Estrella con Impedancia',
};

const TWO_WINDING_VARIANT_WHITELIST = new Set(Object.keys(TWO_WINDING_VARIANT_LABELS));
const TWO_WINDING_PRIMARY_SET = new Set([
  'transfomador-de-2-devanados-Base',
  'transfomador-de-2-devanados-DeltaDelta',
  'transfomador-de-2-devanados-DeltaEstrella',
  'transfomador-de-2-devanados-DeltaEstrella-Aterrizado',
  'transfomador-de-2-devanados-EstrellaEstrella',
  'transfomador-de-2-devanados-Impedancia',
]);

const TWO_WINDING_SECONDARY_SET = new Set([]);

const toEditorSymbolPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  const parts = path.split('/');
  const fileName = parts[parts.length - 1];
  return fileName ? `/assets/editor-symbols/${fileName}` : path;
};

const buildBreakerGroupOptionsFromSymbols = () => {
  const breakerItems = [
    ...(Symbols?.electromagneticSwitches || []),
    ...(Symbols?.powerSwitches || []),
    ...(Symbols?.switches || []),
  ];
  const options = [];

  breakerItems.forEach((item) => {
    const baseLabel = item.label || 'Interruptor';
    const typeLabel = item.type || item.symbolLabel || 'ElectromagneticSwitch';
    const variants = Array.isArray(item.variants) ? item.variants : [];
    const itemContainsInterruptor = (baseLabel || '').toLowerCase().includes('interruptor');

    if (variants.length === 0) {
      if (!itemContainsInterruptor) return;
      options.push({
        alias: item.alias,
        label: baseLabel,
        svgSrc: toEditorSymbolPath(item.svgSrc),
        symbolLabel: typeLabel,
      });
      return;
    }

    variants.forEach((variant) => {
      const variantLabel = variant.label || baseLabel;
      const variantContainsInterruptor = (variantLabel || '').toLowerCase().includes('interruptor');
      if (!itemContainsInterruptor && !variantContainsInterruptor) return;
      options.push({
        alias: variant.alias || item.alias,
        label: variantLabel,
        svgSrc: toEditorSymbolPath(variant.diagramSvgSrc || item.svgSrc),
        symbolLabel: typeLabel,
      });
    });
  });

  return options;
};

const BREAKER_GROUP_OPTIONS = buildBreakerGroupOptionsFromSymbols();

const BREAKER_GROUP_SET = new Set(BREAKER_GROUP_OPTIONS.map((opt) => opt.alias));

const buildFuseGroupOptionsFromSymbols = () => {
  const fuseItems = Symbols?.fuses || [];
  const options = [];
  fuseItems.forEach((item) => {
    const baseLabel = item.label || 'Fusible';
    const typeLabel = item.type || 'Fuse';
    const variants = Array.isArray(item.variants) ? item.variants : [];
    if (variants.length === 0) {
      const normalized = (baseLabel || '').toLowerCase();
      if (normalized.includes('fusible')) {
        options.push({
          alias: item.alias,
          label: baseLabel,
          svgSrc: item.svgSrc,
          symbolLabel: typeLabel,
        });
      }
      return;
    }
    variants.forEach((variant) => {
      const variantLabel = variant.label || baseLabel;
      const normalized = (variantLabel || '').toLowerCase();
      if (!normalized.includes('fusible')) return;
      options.push({
        alias: variant.alias || item.alias,
        label: variantLabel,
        svgSrc: variant.diagramSvgSrc || item.svgSrc,
        symbolLabel: typeLabel,
      });
    });
  });
  return options;
};

const FUSE_GROUP_OPTIONS = buildFuseGroupOptionsFromSymbols();

const FUSE_GROUP_SET = new Set(FUSE_GROUP_OPTIONS.map((opt) => opt.alias));

const buildUtilityGroupOptionsFromSymbols = () => {
  const utilityItems = Symbols?.electricEnergySuppliers || [];
  const utilityLabelsByAlias = {
    'utility-a': 'Proveedor eléctrico A',
    'utility-b': 'Proveedor eléctrico B',
  };
  return utilityItems.map((item) => ({
    alias: item.alias,
    label: utilityLabelsByAlias[item.alias] || item.label || 'Proveedor eléctrico',
    svgSrc: item.svgSrc,
    symbolLabel: item.type || item.symbolLabel || 'ElectricEnergySupplier',
  }));
};

const UTILITY_GROUP_OPTIONS = buildUtilityGroupOptionsFromSymbols();

const UTILITY_GROUP_SET = new Set(UTILITY_GROUP_OPTIONS.map((opt) => opt.alias));

const CABLE_GROUP_OPTIONS = [
  {
    alias: 'conductor-electrico-a',
    label: 'Cable',
    svgSrc: '/symbol-assets/CableA.svg',
    symbolLabel: 'Cable',
  },
  {
    alias: 'conductor-electrico-b',
    label: 'Cable B',
    svgSrc: '/symbol-assets/CableB.svg',
    symbolLabel: 'Cable',
  },
  {
    alias: 'conductor-electrico-c',
    label: 'Cable C',
    svgSrc: '/symbol-assets/CableC.svg',
    symbolLabel: 'Cable',
  },
  {
    alias: 'conductor-electrico-d',
    label: 'Cable D',
    svgSrc: '/symbol-assets/CableD.svg',
    symbolLabel: 'Cable',
  },
  {
    alias: 'conductor-electrico-e',
    label: 'Cable E',
    svgSrc: '/symbol-assets/Electroducto.svg',
    symbolLabel: 'Cable',
  },
  {
    alias: 'conductor-electrico-f',
    label: 'Cable F',
    svgSrc: '/symbol-assets/CableFalso.svg',
    symbolLabel: 'Cable',
  },
];

const CABLE_GROUP_SET = new Set(CABLE_GROUP_OPTIONS.map((opt) => opt.alias));

const getLocalDateText = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const SPANISH_MONTHS = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

const formatSpanishDate = (value) => {
  if (typeof value !== 'string') return '';
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value.trim();
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(day) || monthIndex < 0 || monthIndex >= SPANISH_MONTHS.length) {
    return value.trim();
  }
  return `${day} de ${SPANISH_MONTHS[monthIndex]} del ${year}`;
};

const stripDiacritics = (value = '') =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const normalizeText = (value) =>
  typeof value === 'string' ? stripDiacritics(value).trim().toLowerCase() : '';

const UNIT_SUFFIX_BY_KEY = {
  ratedVoltage: 'V',
  voltage: 'V',
  primaryVoltage: 'V',
  secondaryVoltage: 'V',
  tertiaryVoltage: 'V',
  voltageCurrent: 'V',
  ratedPower: 'kVA',
  power: 'kVA',
  phaseGauge: 'KCMIL',
  length: 'm',
  ratedCurrent: 'A',
  shortCircuitCurrent: 'A',
  breakingCapacity: 'kA',
  protectionBreakingCapacity: 'kA',
  efficiency: '%',
  threePhaseLines: 'A',
  lineasTrifasicas: 'A',
  threePhaseIcc: 'A',
  lineToGroundIcc: 'A',
  threePhaseICC: 'A',
  lineToGroundICC: 'A',
  ampacity: 'A',
  ampacidad: 'A',
  height: 'm',
  lineSeparation: 'm',
  impedance: '%',
  neutralImpedance: 'Ohm',
};

const resolveUnitSuffix = (fieldKey, fieldLabel) => {
  if (!fieldKey && !fieldLabel) return null;
  if (fieldKey && UNIT_SUFFIX_BY_KEY[fieldKey]) {
    return UNIT_SUFFIX_BY_KEY[fieldKey];
  }
  const normalized = normalizeText(fieldLabel);
  if (!normalized) return null;
  if (normalized.includes('voltaje nominal') || normalized === 'voltaje' || normalized.startsWith('voltaje ')) {
    return 'V';
  }
  if (normalized.includes('potencia nominal')) {
    return 'kVA';
  }
  if (normalized.includes('calibre fases')) {
    return 'KCMIL';
  }
  if (normalized === 'longitud') {
    return 'm';
  }
  if (normalized.includes('corriente nominal')) {
    return 'A';
  }
  if (normalized.includes('eficiencia')) {
    return '%';
  }
  if (normalized.includes('corriente de corto circuito') || normalized.includes('corto circuito')) {
    return 'A';
  }
  if (normalized.includes('capacidad interruptiva')) {
    return 'kA';
  }
  if (normalized.includes('lineas trifasicas')) {
    return 'A';
  }
  if (normalized.includes('impedancia de neutro')) {
    return 'Ohm';
  }
  if (normalized.startsWith('impedancia') || normalized.includes('impedancia')) {
    return '%';
  }
  return null;
};

const CURRENT_UNIT_KEYS = new Set([
  'ratedCurrent',
  'shortCircuitCurrent',
  'threePhaseLines',
  'lineasTrifasicas',
  'threePhaseIcc',
  'lineToGroundIcc',
  'threePhaseICC',
  'lineToGroundICC',
  'ampacity',
  'ampacidad',
]);

const VOLTAGE_UNIT_KEYS = new Set([
  'ratedVoltage',
  'voltage',
  'primaryVoltage',
  'secondaryVoltage',
  'tertiaryVoltage',
  'voltageCurrent',
]);

const POWER_UNIT_KEYS = new Set([
  'ratedPower',
  'power',
]);

const useStableSelection = (selector) =>
  useSelector(selector, (prev = [], next = []) => {
    if (prev === next) return true;
    if (!Array.isArray(prev) || !Array.isArray(next)) return false;
    if (prev.length !== next.length) return false;
    for (let i = 0; i < prev.length; i += 1) {
      if (prev[i] !== next[i]) return false;
    }
    return true;
  });

export const Inspector = () => {
  const dispatch = useDispatch();
  const allNodes = useSelector((state) => state.node.nodes) || [];
  const symbolLabelOverrides = useSelector((state) => state.view.symbolLabelOverrides) || {};
  const scene = useSelector((state) => state.inspector.scene);
  const pendingAttributeFocus = useSelector((state) => state.inspector.pendingAttributeFocus);
  const nodes = useSelector((state) => state.node.nodes);
  const selectedNodeIds = useStableSelection((state) => state.node.selectedNodeIds) || [];
  const selectedNodeId = useSelector((state) => state.node.selectedNodeId);
  const selectedEdgeIds = useStableSelection((state) => state.edge.selectedEdgeIds) || [];
  const currentNode = React.useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) || null,
    [nodes, selectedNodeId],
  );
  const hideParameterLabelsDefault = useSelector((state) => !!state.view.hideParameterLabelsDefault);
  const hideAttributeLabelsPref = typeof currentNode?.data?.hideAttributeLabels === 'boolean'
    ? currentNode.data.hideAttributeLabels
    : hideParameterLabelsDefault;
  const [hideParameterLabels, setHideParameterLabels] = React.useState(hideAttributeLabelsPref);
  const { handleVisibilityChange, handleValueChange, setNodes } = useNodeOperations();
  const [draftState, setDraftState] = React.useState({ nodeId: null, values: {}, dirty: {} });
  const normalizeInputValue = React.useCallback((rawValue) => {
    if (rawValue === undefined || rawValue === null) {
      return '';
    }
    return typeof rawValue === 'string' ? rawValue : String(rawValue);
  }, []);
  const getDraftValue = React.useCallback((fieldKey, fallbackValue = '') => {
    if (!fieldKey) {
      return fallbackValue;
    }
    if (draftState.nodeId !== currentNode?.id) {
      return fallbackValue;
    }
    if (Object.prototype.hasOwnProperty.call(draftState.values, fieldKey)) {
      return draftState.values[fieldKey];
    }
    return fallbackValue;
  }, [draftState, currentNode?.id]);

  const buildTwoWindingOptions = React.useCallback(
    (allowedSet) => {
      if (!currentNode || currentNode.data?.symbolLabel !== 'TwoWindingTransformer') return [];
      const variants = Array.isArray(currentNode.data?.variants) ? currentNode.data.variants : [];
      return variants
        .filter((variant) => variant?.alias && TWO_WINDING_VARIANT_WHITELIST.has(variant.alias) && allowedSet.has(variant.alias))
        .map((variant) => ({
          alias: variant.alias,
          label: TWO_WINDING_VARIANT_LABELS[variant.alias] || (variant.label || variant.alias),
          selected: !!variant.selected,
        }));
    },
    [currentNode],
  );

  const twoWindingPrimaryOptions = React.useMemo(
    () => buildTwoWindingOptions(TWO_WINDING_PRIMARY_SET),
    [buildTwoWindingOptions]
  );

  const twoWindingSecondaryOptions = React.useMemo(
    () => buildTwoWindingOptions(TWO_WINDING_SECONDARY_SET),
    [buildTwoWindingOptions]
  );

  const breakerGroupOptions = React.useMemo(() => BREAKER_GROUP_OPTIONS, []);
  const fuseGroupOptions = React.useMemo(() => FUSE_GROUP_OPTIONS, []);
  const utilityGroupOptions = React.useMemo(() => UTILITY_GROUP_OPTIONS, []);
  const cableGroupOptions = React.useMemo(() => CABLE_GROUP_OPTIONS, []);

  const handleTwoWindingVariantChange = React.useCallback((nextAlias) => {
    if (!nextAlias || !currentNode?.id) return;
    const nextVariants = Array.isArray(currentNode.data?.variants)
      ? currentNode.data.variants.map((variant) => ({
          ...variant,
          selected: variant.alias === nextAlias,
        }))
      : [];
    const selectedVariant =
      nextVariants.find((variant) => variant.alias === nextAlias) ||
      nextVariants.find((variant) => variant.selected) ||
      nextVariants[0];
    const baseAlias = currentNode.data?.baseAlias || currentNode.data?.alias;
    const resolvedAlias = selectedVariant?.alias || currentNode.data?.alias || baseAlias;
    const resolvedSvg = selectedVariant?.diagramSvgSrc || currentNode.data?.svgSrc;
    const nextNodeData = {
      ...(currentNode.data || {}),
      alias: resolvedAlias,
      baseAlias,
      variants: nextVariants,
      svgSrc: resolvedSvg,
    };
    const inspectorInfo = resolveInspectorForNode(nextNodeData, symbolLabelOverrides);
    const templateAttributes = inspectorInfo.attributes || {};
    const existingAttributes = currentNode.data?.attributes || {};
    const missingAttributes = Object.entries(templateAttributes).reduce((acc, [key, tmplAttr]) => {
      const current = existingAttributes[key];
      if (!current) {
        acc[key] = tmplAttr;
      } else if (tmplAttr && tmplAttr.label && !current.label) {
        acc[key] = { ...current, label: tmplAttr.label };
      }
      return acc;
    }, {});
    setNodes((prevNodes) =>
      prevNodes.map((node) => {
        if (node.id !== currentNode.id) return node;
        return {
          ...node,
          data: {
            ...(node.data || {}),
            ...nextNodeData,
          },
        };
      })
    );
    if (Object.keys(missingAttributes).length > 0) {
      dispatch(setNodeAttributes({
        nodeId: currentNode.id,
        attributes: missingAttributes,
        replace: false,
      }));
    }
    dispatch(setScene({ scene: inspectorInfo.inspectorId || INSPECTOR_IDS.DEFAULT }));
  }, [currentNode, dispatch, setNodes, symbolLabelOverrides]);

  const handleBreakerVariantChange = React.useCallback(
    (nextAlias) => {
      if (!nextAlias || !currentNode?.id) return;
      const target = breakerGroupOptions.find((opt) => opt.alias === nextAlias);
      if (!target) return;
      const nextNodeData = {
        ...(currentNode.data || {}),
        alias: target.alias,
        baseAlias: target.alias,
        svgSrc: target.svgSrc,
        symbolLabel: target.symbolLabel,
        variants: [],
      };
      const inspectorInfo = resolveInspectorForNode(nextNodeData, symbolLabelOverrides);
      const templateAttributes = inspectorInfo.attributes || {};
      const existingAttributes = currentNode.data?.attributes || {};
      const missingAttributes = Object.entries(templateAttributes).reduce((acc, [key, tmplAttr]) => {
        const current = existingAttributes[key];
        if (!current) {
          acc[key] = tmplAttr;
        } else if (tmplAttr && tmplAttr.label && !current.label) {
          acc[key] = { ...current, label: tmplAttr.label };
        }
        return acc;
      }, {});
      setNodes((prevNodes) =>
        prevNodes.map((node) => {
          if (node.id !== currentNode.id) return node;
          return {
            ...node,
            data: {
              ...(node.data || {}),
              ...nextNodeData,
            },
          };
        })
      );
      if (Object.keys(missingAttributes).length > 0) {
        dispatch(setNodeAttributes({
          nodeId: currentNode.id,
          attributes: missingAttributes,
          replace: false,
        }));
      }
      dispatch(setScene({ scene: inspectorInfo.inspectorId || INSPECTOR_IDS.DEFAULT }));
    },
    [breakerGroupOptions, currentNode, dispatch, setNodes, symbolLabelOverrides]
  );

  const handleFuseVariantChange = React.useCallback(
    (nextAlias) => {
      if (!nextAlias || !currentNode?.id) return;
      const target = fuseGroupOptions.find((opt) => opt.alias === nextAlias);
      if (!target) return;
      const nextNodeData = {
        ...(currentNode.data || {}),
        alias: target.alias,
        baseAlias: target.alias,
        svgSrc: target.svgSrc,
        symbolLabel: target.symbolLabel,
        variants: [],
      };
      const inspectorInfo = resolveInspectorForNode(nextNodeData, symbolLabelOverrides);
      const templateAttributes = inspectorInfo.attributes || {};
      const existingAttributes = currentNode.data?.attributes || {};
      const missingAttributes = Object.entries(templateAttributes).reduce((acc, [key, tmplAttr]) => {
        const current = existingAttributes[key];
        if (!current) {
          acc[key] = tmplAttr;
        } else if (tmplAttr && tmplAttr.label && !current.label) {
          acc[key] = { ...current, label: tmplAttr.label };
        }
        return acc;
      }, {});
      setNodes((prevNodes) =>
        prevNodes.map((node) => {
          if (node.id !== currentNode.id) return node;
          return {
            ...node,
            data: {
              ...(node.data || {}),
              ...nextNodeData,
            },
          };
        })
      );
      if (Object.keys(missingAttributes).length > 0) {
        dispatch(setNodeAttributes({
          nodeId: currentNode.id,
          attributes: missingAttributes,
          replace: false,
        }));
      }
      dispatch(setScene({ scene: inspectorInfo.inspectorId || INSPECTOR_IDS.DEFAULT }));
    },
    [currentNode, dispatch, fuseGroupOptions, setNodes, symbolLabelOverrides]
  );

  const handleUtilityVariantChange = React.useCallback(
    (nextAlias) => {
      if (!nextAlias || !currentNode?.id) return;
      const target = utilityGroupOptions.find((opt) => opt.alias === nextAlias);
      if (!target) return;
      const nextNodeData = {
        ...(currentNode.data || {}),
        alias: target.alias,
        baseAlias: target.alias,
        svgSrc: target.svgSrc,
        symbolLabel: target.symbolLabel,
        variants: [],
      };
      const inspectorInfo = resolveInspectorForNode(nextNodeData, symbolLabelOverrides);
      const templateAttributes = inspectorInfo.attributes || {};
      const existingAttributes = currentNode.data?.attributes || {};
      const missingAttributes = Object.entries(templateAttributes).reduce((acc, [key, tmplAttr]) => {
        const current = existingAttributes[key];
        if (!current) {
          acc[key] = tmplAttr;
        } else if (tmplAttr && tmplAttr.label && !current.label) {
          acc[key] = { ...current, label: tmplAttr.label };
        }
        return acc;
      }, {});
      setNodes((prevNodes) =>
        prevNodes.map((node) => {
          if (node.id !== currentNode.id) return node;
          return {
            ...node,
            data: {
              ...(node.data || {}),
              ...nextNodeData,
            },
          };
        })
      );
      if (Object.keys(missingAttributes).length > 0) {
        dispatch(setNodeAttributes({
          nodeId: currentNode.id,
          attributes: missingAttributes,
          replace: false,
        }));
      }
      dispatch(setScene({ scene: inspectorInfo.inspectorId || INSPECTOR_IDS.DEFAULT }));
    },
    [currentNode, dispatch, setNodes, utilityGroupOptions, symbolLabelOverrides]
  );

  const handleCableVariantChange = React.useCallback(
    (nextAlias) => {
      if (!nextAlias || !currentNode?.id) return;
      const target = cableGroupOptions.find((opt) => opt.alias === nextAlias);
      if (!target) return;
      const nextNodeData = {
        ...(currentNode.data || {}),
        alias: target.alias,
        baseAlias: target.alias,
        svgSrc: target.svgSrc,
        symbolLabel: target.symbolLabel,
        variants: [],
      };
      const inspectorInfo = resolveInspectorForNode(nextNodeData, symbolLabelOverrides);
      const templateAttributes = inspectorInfo.attributes || {};
      const existingAttributes = currentNode.data?.attributes || {};
      const missingAttributes = Object.entries(templateAttributes).reduce((acc, [key, tmplAttr]) => {
        const current = existingAttributes[key];
        if (!current) {
          acc[key] = tmplAttr;
        } else if (tmplAttr && tmplAttr.label && !current.label) {
          acc[key] = { ...current, label: tmplAttr.label };
        }
        return acc;
      }, {});
      setNodes((prevNodes) =>
        prevNodes.map((node) => {
          if (node.id !== currentNode.id) return node;
          return {
            ...node,
            data: {
              ...(node.data || {}),
              ...nextNodeData,
            },
          };
        })
      );
      if (Object.keys(missingAttributes).length > 0) {
        dispatch(setNodeAttributes({
          nodeId: currentNode.id,
          attributes: missingAttributes,
          replace: false,
        }));
      }
      dispatch(setScene({ scene: inspectorInfo.inspectorId || INSPECTOR_IDS.DEFAULT }));
    },
    [cableGroupOptions, currentNode, dispatch, setNodes, symbolLabelOverrides]
  );

  const fieldRefs = React.useRef({});
  const registerFieldRef = React.useCallback((key) => (el) => {
    if (!key) return;
    if (el) {
      fieldRefs.current[key] = el;
    } else {
      delete fieldRefs.current[key];
    }
  }, []);
  React.useEffect(() => {
    if (!currentNode?.id) {
      setDraftState({ nodeId: null, values: {}, dirty: {} });
      return;
    }
    const attrs = currentNode?.data?.attributes || {};
    setDraftState((prev) => {
      if (prev.nodeId !== currentNode.id) {
        const values = {};
        Object.entries(attrs).forEach(([key, attribute]) => {
          values[key] = normalizeInputValue(attribute?.value);
        });
        return { nodeId: currentNode.id, values, dirty: {} };
      }
      let changed = false;
      const nextValues = { ...prev.values };
      const nextDirty = { ...prev.dirty };
      Object.entries(attrs).forEach(([key, attribute]) => {
        const attrValue = normalizeInputValue(attribute?.value);
        if (!prev.dirty[key] && nextValues[key] !== attrValue) {
          nextValues[key] = attrValue;
          changed = true;
        }
        if (prev.dirty[key] && attrValue === nextValues[key]) {
          delete nextDirty[key];
          changed = true;
        }
      });
      Object.keys(nextValues).forEach((key) => {
        if (!attrs[key]) {
          delete nextValues[key];
          if (nextDirty[key]) {
            delete nextDirty[key];
          }
          changed = true;
        }
      });
      if (changed) {
        return { nodeId: prev.nodeId, values: nextValues, dirty: nextDirty };
      }
      return prev;
    });
  }, [currentNode?.id, currentNode?.data?.attributes, normalizeInputValue]);
  const markDraftDirty = React.useCallback((fieldKey, nextValue) => {
    if (!fieldKey) return;
    setDraftState((prev) => {
      if (prev.nodeId !== currentNode?.id) {
        return prev;
      }
      const nextValues = { ...prev.values, [fieldKey]: normalizeInputValue(nextValue) };
      const nextDirty = { ...prev.dirty, [fieldKey]: true };
      return { ...prev, values: nextValues, dirty: nextDirty };
    });
  }, [currentNode?.id, normalizeInputValue]);
  const resetDraftValue = React.useCallback((fieldKey, nextValue) => {
    if (!fieldKey) return;
    setDraftState((prev) => {
      if (prev.nodeId !== currentNode?.id) {
        return prev;
      }
      const nextValues = { ...prev.values, [fieldKey]: normalizeInputValue(nextValue) };
      const nextDirty = { ...prev.dirty };
      delete nextDirty[fieldKey];
      return { ...prev, values: nextValues, dirty: nextDirty };
    });
  }, [currentNode?.id, normalizeInputValue]);
  const hasDuplicateNombre = React.useCallback((nextValue) => {
    if (!selectedNodeId) return false;
    const normalized = typeof nextValue === 'string' ? nextValue.trim().toLowerCase() : '';
    if (!normalized) return false;
    return allNodes.some((node) => {
      if (!node || node.id === selectedNodeId) return false;
      const otherValue = node?.data?.attributes?.nombre?.value;
      return typeof otherValue === 'string' && otherValue.trim().toLowerCase() === normalized;
    });
  }, [allNodes, selectedNodeId]);
  const commitFieldValue = React.useCallback((fieldKey, nextValue) => {
    if (!selectedNodeId) {
      return;
    }
    if (fieldKey === 'nombre' && hasDuplicateNombre(nextValue)) {
      const previousValue = currentNode?.data?.attributes?.[fieldKey]?.value ?? '';
      resetDraftValue(fieldKey, previousValue);
      dispatch(addAlert({
        duration: 2500,
        severity: 'error',
        message: 'Dos símbolos no pueden tener el mismo nombre',
      }));
      return;
    }
    handleValueChange(selectedNodeId, fieldKey, nextValue);
  }, [currentNode?.data?.attributes, dispatch, handleValueChange, hasDuplicateNombre, resetDraftValue, selectedNodeId]);
  const createFieldChangeHandler = React.useCallback((fieldKey) => (event) => {
    const nextValue = event.target.value;
    markDraftDirty(fieldKey, nextValue);
    if (!selectedNodeId || fieldKey === 'nombre') {
      return;
    }
    handleValueChange(selectedNodeId, fieldKey, nextValue);
  }, [handleValueChange, markDraftDirty, selectedNodeId]);
  const createFieldBlurHandler = React.useCallback((fieldKey) => (event) => {
    if (fieldKey !== 'nombre') {
      return;
    }
    commitFieldValue(fieldKey, event.target.value);
  }, [commitFieldValue]);
  const createFieldKeyDownHandler = React.useCallback((fieldKey) => (event) => {
    if (fieldKey !== 'nombre') {
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      commitFieldValue(fieldKey, event.currentTarget.value);
      if (typeof event.currentTarget.blur === 'function') {
        event.currentTarget.blur();
      }
    }
  }, [commitFieldValue]);

  React.useEffect(() => {
    if (!pendingAttributeFocus) return;
    if (pendingAttributeFocus.nodeId !== selectedNodeId) {
      return;
    }
    const key = pendingAttributeFocus.attributeName;
    if (!key) {
      dispatch(clearAttributeFocus());
      return;
    }
    const target = fieldRefs.current[key];
    if (target && typeof target.focus === 'function') {
      target.focus();
      if (typeof target.select === 'function') {
        target.select();
      }
    }
    dispatch(clearAttributeFocus());
  }, [pendingAttributeFocus, selectedNodeId, dispatch]);
  React.useEffect(() => {
    setHideParameterLabels(hideAttributeLabelsPref);
  }, [hideAttributeLabelsPref, currentNode?.id]);
  // Derive a human-friendly inspector label from the node's symbol type
  const symbolType = currentNode?.data?.symbolLabel;
  const nodeLabel = currentNode?.data?.label;
  const isTextBoxInspector = scene === INSPECTOR_IDS.TEXT_BOX;
  const inspectorLabel = React.useMemo(() => {
    if (!symbolType) return null;
    if (symbolType === 'ElectromagneticSwitch') {
      if (typeof nodeLabel === 'string' && nodeLabel.toLowerCase().includes('interruptor baja tensión')) {
        return 'Interruptor Baja Tensión';
      }
      return 'Interruptor';
    }
    for (const group of Object.values(Symbols)) {
      const items = Array.isArray(group) ? group : [];
      const found = items.find((it) => it?.type === symbolType);
      if (found) return found.label || symbolType;
    }
    return symbolType;
  }, [symbolType, nodeLabel]);
  
  // If an edge is selected and no nodes are selected, render the Connecting Cable inspector
  if (selectedNodeIds.length === 0 && selectedEdgeIds.length > 0) {
    return (
      <div className="ctn-inspector">
        <ConnectingCableInspector />
      </div>
    );
  }

  if (selectedNodeIds.length === 0) {
    return (
      <div className="ctn-inspector">
        <DefaultInspector />
      </div>
    );
  }

  // When multiple nodes are selected, keep the Symbols library visible (no inspector content).
  if (selectedNodeIds.length > 1) {
    return null;
  }

  if (!currentNode || !currentNode.data?.attributes) {
    return <div>No Node Selected</div>;
  }

  const renderHideLabelsToggle = (key = 'hide-parameter-toggle') => (
    <div key={key} className="inspector__field-toggle">
      <label className="inspector__field-toggle-control">
        <input
          type="checkbox"
          className="inspector__field-toggle-checkbox"
          checked={hideParameterLabels}
          onChange={(event) => {
            const nextValue = event.target.checked;
            setHideParameterLabels(nextValue);
            if (currentNode?.id) {
              dispatch(setNodeHideAttributeLabels({ nodeId: currentNode.id, hideAttributeLabels: nextValue }));
            }
          }}
        />
        <span>Esconder nombre de los parametros</span>
      </label>
    </div>
  );

  const extraProps = {
    ...(currentNode.data.isSubdiagram && currentNode.data.subdiagramId
      ? { subdiagramId: currentNode.data.subdiagramId }
      : {}),
    ...(currentNode.data.parentId ? { parentId: currentNode.data.parentId } : {})
  };

  const inspectorDefinition = getInspectorDefinition(scene);
  const fieldConfigs = Array.isArray(inspectorDefinition?.fields) ? inspectorDefinition.fields : [];
  const customRenderer = inspectorDefinition?.customRenderer;
  const outOfServiceDate =
    currentNode?.data?.outOfService === true
      ? (typeof currentNode.data.outOfServiceDate === 'string' && currentNode.data.outOfServiceDate.trim()
          ? currentNode.data.outOfServiceDate.trim()
          : getLocalDateText())
      : null;
  const outOfServiceDateLabel = outOfServiceDate ? formatSpanishDate(outOfServiceDate) : null;
  const hasNombreFieldInConfig = fieldConfigs.some((field) => field?.key === 'nombre');
  const disableCurrentUnitToggle = scene === INSPECTOR_IDS.FUSES;

  let CustomRenderer = null;
  if (customRenderer === 'distributionBoard') {
    CustomRenderer = DistributionBoardInspector;
  } else if (customRenderer === 'box') {
    CustomRenderer = BoxInspector;
  }

  const renderAttributeRow = (key, attribute, fieldOptions = {}) => {
    if (!key) {
      return null;
    }
    if (isTextBoxInspector && (key === 'backgroundColor' || key === 'textColor')) {
      return null;
    }
    const attr = attribute || (currentNode.data.attributes ? currentNode.data.attributes[key] : null);
    if (!attr) {
      return null;
    }
    if (attr.systemModified) {
      return null;
    }
    const fieldType = fieldOptions.type || 'text';
    const defaultLabel = fieldOptions.label || key;
    const label = attr.label;
    const baseValue = attr.value ?? '';
    let value = getDraftValue(key, normalizeInputValue(baseValue));
    const visibilityUserModified = attr.visibilityUserModified === true;
    const isVisible = visibilityUserModified ? !!attr.isVisible : true;
    const showVisibilityToggle = !isTextBoxInspector;

    const fieldClassName = `inspector__field ${isVisible ? 'inspector__field--visible' : 'inspector__field--hidden'}`;
    const isGaugeField = key === 'phaseGauge' || key === 'neutralGauge' || key === 'groundGauge';
    const isI2tField = key === 'std' || key === 'gfd';
    const isLengthField = key === 'length';
    const baseResolvedUnitSuffix = resolveUnitSuffix(fieldOptions.key || key, label);
    const isVoltageField = VOLTAGE_UNIT_KEYS.has(fieldOptions.key || key) || baseResolvedUnitSuffix === 'V';
    const isPowerField = POWER_UNIT_KEYS.has(fieldOptions.key || key) || baseResolvedUnitSuffix === 'kVA';
    const resolvedUnitSuffix =
      fieldType === 'textarea'
      || fieldType === 'color'
      || isGaugeField
      || isI2tField
      || isLengthField
      || isVoltageField
      || isPowerField
        ? null
        : baseResolvedUnitSuffix;
    const isCurrentField = CURRENT_UNIT_KEYS.has(fieldOptions.key || key) || resolvedUnitSuffix === 'A';
    const unitSuffix = isCurrentField ? null : resolvedUnitSuffix;
    const gaugeUnit = isGaugeField
      ? (['AWG', 'KCMIL', 'mm2'].includes(attr?.unitSuffix) ? attr.unitSuffix : 'KCMIL')
      : null;
    const currentUnit = isCurrentField
      ? (['A', 'kA'].includes(attr?.unitSuffix) ? attr.unitSuffix : 'A')
      : null;
    const displayedCurrentUnit = disableCurrentUnitToggle ? 'A' : currentUnit;
    const i2tUnit = isI2tField
      ? (attr?.unitSuffix === 'I2t OFF' ? 'I2t OFF' : 'I2t ON')
      : null;
    const lengthUnit = isLengthField ? (['m', 'km', 'pies'].includes(attr?.unitSuffix) ? attr.unitSuffix : 'm') : null;
    const voltageUnit = isVoltageField
      ? (['V', 'kV'].includes(attr?.unitSuffix) ? attr.unitSuffix : 'V')
      : null;
    const powerUnit = isPowerField
      ? (['HP', 'kW', 'kVA'].includes(attr?.unitSuffix) ? attr.unitSuffix : 'kVA')
      : null;
    if (fieldType === 'color') {
      if (typeof value !== 'string' || !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim())) {
        value = '#111827';
      }
    }

    const handleFieldChange = createFieldChangeHandler(key);
    const handleFieldBlur = createFieldBlurHandler(key);
    const handleFieldKeyDown = createFieldKeyDownHandler(key);

    const handleLabelCommit = (nextLabel) => {
      if (!selectedNodeId) return;
      const resolvedLabel = nextLabel ?? defaultLabel;
      const symbolKey = getNodeLabelOverrideKey(currentNode?.data, scene);
      if (symbolKey) {
        dispatch(setSymbolLabelOverride({
          symbolKey,
          fieldKey: key,
          label: resolvedLabel === defaultLabel ? null : resolvedLabel,
        }));
      }
      setNodes((prevNodes) => prevNodes.map((node) => {
        const nodeKey = getNodeLabelOverrideKey(
          node?.data,
          resolveInspectorForNode(node?.data || {}, symbolLabelOverrides).inspectorId
        );
        if (!symbolKey || nodeKey !== symbolKey) {
          return node;
        }
        const existingAttributes = node?.data?.attributes || {};
        const existingAttribute = existingAttributes[key];
        if (!existingAttribute) {
          return node;
        }
        return {
          ...node,
          data: {
            ...(node.data || {}),
            attributes: {
              ...existingAttributes,
              [key]: {
                ...existingAttribute,
                label: resolvedLabel,
              },
            },
          },
        };
      }));
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_INSPECTOR }));
    };

    return (
      <div key={key} className={fieldClassName} data-field-key={key}>
        <EditableInspectorLabel
          label={label}
          defaultLabel={defaultLabel}
          onCommit={handleLabelCommit}
        />
        <div className="inspector__field-controls">
          <div className="inspector__field-input">
            {fieldType === 'textarea' ? (
              <textarea
                className="inspector__field-input-element inspector__field-input-element--textarea"
                value={value}
                onChange={handleFieldChange}
                placeholder={label}
                rows={3}
                ref={registerFieldRef(key)}
              />
            ) : (
              <input
                type={fieldType === 'color' ? 'color' : 'text'}
                className={`inspector__field-input-element${fieldType === 'color' ? ' inspector__field-input-element--color' : ''}`}
                value={value}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                onKeyDown={handleFieldKeyDown}
                placeholder={label}
                ref={registerFieldRef(key)}
              />
            )}
            {unitSuffix ? (
              <span className="inspector__field-input-unit" aria-hidden="true">
                {unitSuffix}
              </span>
            ) : null}
            {isGaugeField ? (
              <button
                type="button"
                className="inspector__field-unit-toggle"
                onClick={() => {
                  if (!selectedNodeId) return;
                  const cycle = ['KCMIL', 'AWG', 'mm2'];
                  const currentIndex = cycle.indexOf(gaugeUnit);
                  const nextUnit = cycle[(currentIndex + 1) % cycle.length];
                  dispatch(setNodeAttributes({
                    nodeId: selectedNodeId,
                    attributes: {
                      [key]: {
                        ...(currentNode.data.attributes?.[key] || {}),
                        unitSuffix: nextUnit,
                      },
                    },
                  }));
                }}
              >
                {gaugeUnit}
              </button>
            ) : null}
            {isPowerField ? (
              <button
                type="button"
                className="inspector__field-unit-toggle"
                onClick={() => {
                  if (!selectedNodeId) return;
                  const cycle = ['HP', 'kW', 'kVA'];
                  const currentIndex = cycle.indexOf(powerUnit);
                  const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % cycle.length;
                  const nextUnit = cycle[nextIndex];
                  dispatch(setNodeAttributes({
                    nodeId: selectedNodeId,
                    attributes: {
                      [key]: {
                        ...(currentNode.data.attributes?.[key] || {}),
                        unitSuffix: nextUnit,
                      },
                    },
                  }));
                }}
              >
                {powerUnit}
              </button>
            ) : null}
            {isVoltageField ? (
              <button
                type="button"
                className="inspector__field-unit-toggle"
                onClick={() => {
                  if (!selectedNodeId) return;
                  const nextUnit = voltageUnit === 'kV' ? 'V' : 'kV';
                  dispatch(setNodeAttributes({
                    nodeId: selectedNodeId,
                    attributes: {
                      [key]: {
                        ...(currentNode.data.attributes?.[key] || {}),
                        unitSuffix: nextUnit,
                      },
                    },
                  }));
                }}
              >
                {voltageUnit}
              </button>
            ) : null}
            {isCurrentField ? (
              disableCurrentUnitToggle ? (
                <span className="inspector__field-input-unit" aria-hidden="true">
                  {displayedCurrentUnit}
                </span>
              ) : (
                <button
                  type="button"
                  className="inspector__field-unit-toggle"
                  onClick={() => {
                    if (!selectedNodeId) return;
                    const nextUnit = currentUnit === 'kA' ? 'A' : 'kA';
                    dispatch(setNodeAttributes({
                      nodeId: selectedNodeId,
                      attributes: {
                        [key]: {
                          ...(currentNode.data.attributes?.[key] || {}),
                          unitSuffix: nextUnit,
                        },
                      },
                    }));
                  }}
                >
                  {displayedCurrentUnit}
                </button>
              )
            ) : null}
            {isI2tField ? (
              <button
                type="button"
                className="inspector__field-unit-toggle"
                onClick={() => {
                  if (!selectedNodeId) return;
                  const nextUnit = i2tUnit === 'I2t ON' ? 'I2t OFF' : 'I2t ON';
                  dispatch(setNodeAttributes({
                    nodeId: selectedNodeId,
                    attributes: {
                      [key]: {
                        ...(currentNode.data.attributes?.[key] || {}),
                        unitSuffix: nextUnit,
                      },
                    },
                  }));
                }}
              >
                <span>
                  I<sup>2</sup>t {i2tUnit === 'I2t OFF' ? 'OFF' : 'ON'}
                </span>
              </button>
            ) : null}
            {isLengthField ? (
              <button
                type="button"
                className="inspector__field-unit-toggle"
                onClick={() => {
                  if (!selectedNodeId) return;
                  const cycle = ['m', 'km', 'pies'];
                  const currentIndex = cycle.indexOf(lengthUnit);
                  const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % cycle.length;
                  const nextUnit = cycle[nextIndex];
                  dispatch(setNodeAttributes({
                    nodeId: selectedNodeId,
                    attributes: {
                      [key]: {
                        ...(currentNode.data.attributes?.[key] || {}),
                        unitSuffix: nextUnit,
                      },
                    },
                  }));
                }}
              >
                {lengthUnit}
              </button>
            ) : null}
          </div>
          {showVisibilityToggle ? (
            <button
              type="button"
              className="inspector__field-visibility-toggle"
              onClick={() => handleVisibilityChange(selectedNodeId, key, !isVisible)}
              title={isVisible ? 'Ocultar del plano' : 'Mostrar en el plano'}
            >
              <img
                src={isVisible ? visibleIcon : hiddenIcon}
                alt={isVisible ? 'Visible en plano' : 'Oculto en plano'}
                className="inspector__field-visibility-icon"
              />
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const renderTwoWindingVariantSelector = () => {
    const currentAlias =
      (Array.isArray(currentNode?.data?.variants)
        ? currentNode.data.variants.find((variant) => variant.selected)?.alias ||
          currentNode.data.variants[0]?.alias
        : null) || currentNode?.data?.alias;
    const isSecondary = currentAlias && TWO_WINDING_SECONDARY_SET.has(currentAlias);
    const options = isSecondary ? twoWindingSecondaryOptions : twoWindingPrimaryOptions;
    if (!options.length) return null;
    const selectedVariantAlias =
      options.find((variant) => variant.selected)?.alias ||
      options[0]?.alias ||
      '';

    return (
      <div className="inspector__field" key="two-winding-variant">
        <div className="inspector__field-label">Símbolo</div>
        <div className="inspector__field-controls">
          <div className="inspector__field-input">
            <select
              className="inspector__select"
              value={selectedVariantAlias}
              onChange={(event) => handleTwoWindingVariantChange(event.target.value)}
            >
              {options.map((variant) => (
                <option key={variant.alias} value={variant.alias}>
                  {variant.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };

  const renderBreakerVariantSelector = () => {
    const currentAlias =
      (Array.isArray(currentNode?.data?.variants)
        ? currentNode.data.variants.find((variant) => variant.selected)?.alias ||
          currentNode.data.variants[0]?.alias
        : null) || currentNode?.data?.alias;
    if (!currentAlias || !BREAKER_GROUP_SET.has(currentAlias)) return null;
    const options = breakerGroupOptions;
    const selectedAlias = options.find((opt) => opt.alias === currentAlias)?.alias || options[0]?.alias || '';
    if (!selectedAlias) return null;

    return (
      <div className="inspector__field" key="breaker-variant">
        <div className="inspector__field-label">Símbolo</div>
        <div className="inspector__field-controls">
          <div className="inspector__field-input">
            <select
              className="inspector__select"
              value={selectedAlias}
              onChange={(event) => handleBreakerVariantChange(event.target.value)}
            >
              {options.map((opt) => (
                <option key={opt.alias} value={opt.alias}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };

  const renderFuseVariantSelector = () => {
    const currentAlias =
      (Array.isArray(currentNode?.data?.variants)
        ? currentNode.data.variants.find((variant) => variant.selected)?.alias ||
          currentNode.data.variants[0]?.alias
        : null) || currentNode?.data?.alias;
    if (!currentAlias || !FUSE_GROUP_SET.has(currentAlias)) return null;
    const options = fuseGroupOptions;
    const selectedAlias = options.find((opt) => opt.alias === currentAlias)?.alias || options[0]?.alias || '';
    if (!selectedAlias) return null;

    return (
      <div className="inspector__field" key="fuse-variant">
        <div className="inspector__field-label">Símbolo</div>
        <div className="inspector__field-controls">
          <div className="inspector__field-input">
            <select
              className="inspector__select"
              value={selectedAlias}
              onChange={(event) => handleFuseVariantChange(event.target.value)}
            >
              {options.map((opt) => (
                <option key={opt.alias} value={opt.alias}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };

  const renderUtilityVariantSelector = () => {
    const currentAlias =
      (Array.isArray(currentNode?.data?.variants)
        ? currentNode.data.variants.find((variant) => variant.selected)?.alias ||
          currentNode.data.variants[0]?.alias
        : null) || currentNode?.data?.alias;
    if (!currentAlias || !UTILITY_GROUP_SET.has(currentAlias)) return null;
    const options = utilityGroupOptions;
    const selectedAlias = options.find((opt) => opt.alias === currentAlias)?.alias || options[0]?.alias || '';
    if (!selectedAlias) return null;

    return (
      <div className="inspector__field" key="utility-variant">
        <div className="inspector__field-label">Símbolo</div>
        <div className="inspector__field-controls">
          <div className="inspector__field-input">
            <select
              className="inspector__select"
              value={selectedAlias}
              onChange={(event) => handleUtilityVariantChange(event.target.value)}
            >
              {options.map((opt) => (
                <option key={opt.alias} value={opt.alias}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };

  const renderCableVariantSelector = () => {
    const currentAlias =
      (Array.isArray(currentNode?.data?.variants)
        ? currentNode.data.variants.find((variant) => variant.selected)?.alias ||
          currentNode.data.variants[0]?.alias
        : null) || currentNode?.data?.alias;
    if (!currentAlias || !CABLE_GROUP_SET.has(currentAlias)) return null;
    const options = cableGroupOptions;
    const selectedAlias = options.find((opt) => opt.alias === currentAlias)?.alias || options[0]?.alias || '';
    if (!selectedAlias) return null;

    return (
      <div className="inspector__field" key="cable-variant">
        <div className="inspector__field-label">Símbolo</div>
        <div className="inspector__field-controls">
          <div className="inspector__field-input">
            <select
              className="inspector__select"
              value={selectedAlias}
              onChange={(event) => handleCableVariantChange(event.target.value)}
            >
              {options.map((opt) => (
                <option key={opt.alias} value={opt.alias}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };

  const nombreAttr = currentNode.data.attributes?.nombre;
  const shouldRenderNombre = !isTextBoxInspector && !!nombreAttr;
  const configFieldElements = [];
  fieldConfigs.forEach((field) => {
    if (!field) {
      return;
    }
    const key = field.key || field.label;
    if (field.type === 'section') {
      configFieldElements.push(
        <div key={key} className="inspector__section">
          <div className="inspector__section-label">{field.label}</div>
        </div>,
      );
      return;
    }
    if (field.renderMode === 'custom') {
      return;
    }
    const element = renderAttributeRow(field.key, currentNode.data.attributes?.[field.key], field);
    if (element) {
      configFieldElements.push(element);
    }
  });

  const leadingFieldElements = [];
  const hideToggleElement = shouldRenderNombre && customRenderer !== 'box' ? renderHideLabelsToggle() : null;
  const breakerSelector = renderBreakerVariantSelector();
  const fuseSelector = renderFuseVariantSelector();
  const utilitySelector = renderUtilityVariantSelector();
  const cableSelector = renderCableVariantSelector();
  const twoWindingSelector = renderTwoWindingVariantSelector();
  if (hideToggleElement) {
    leadingFieldElements.push(hideToggleElement);
  }
  if (breakerSelector) {
    leadingFieldElements.push(breakerSelector);
  }
  if (fuseSelector) {
    leadingFieldElements.push(fuseSelector);
  }
  if (utilitySelector) {
    leadingFieldElements.push(utilitySelector);
  }
  if (cableSelector) {
    leadingFieldElements.push(cableSelector);
  }
  if (twoWindingSelector) {
    leadingFieldElements.push(twoWindingSelector);
  }
  if (shouldRenderNombre && !hasNombreFieldInConfig) {
    const nombreElement = renderAttributeRow('nombre', nombreAttr, {
      label: nombreAttr.label || 'Nombre',
    });
    if (nombreElement) {
      leadingFieldElements.push(nombreElement);
    }
  }

  if (customRenderer === 'distributionBoard' && CustomRenderer) {
    return (
      <div className="ctn-inspector">
        <CustomRenderer {...extraProps} />
      </div>
    );
  }

  return (
    <div className="ctn-inspector">
      <div className="inspector__title-controls">
        <div className="inspector__title">{inspectorLabel || scene}</div>
      </div>
      {outOfServiceDateLabel ? (
        <div className="inspector__service-status">
          Fuera de servicio desde el día {outOfServiceDateLabel}
        </div>
      ) : null}

      <div className="inspector__divider"></div>

      {leadingFieldElements.length > 0 ? leadingFieldElements : null}

      {CustomRenderer ? <CustomRenderer {...extraProps} /> : null}

      {configFieldElements.length > 0 ? (
        <div>
          {configFieldElements}
        </div>
      ) : null}
    </div>
  );
};

export default Inspector;
